import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	changePass,
	validatePassChange,
} from "../repositories/MillSetupRepository";

interface UseChangePassOptions {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
	onWarning?: (message: string) => Promise<boolean>;
}

export const useChangePass = ({
	onSuccess,
	onError,
	onWarning,
}: UseChangePassOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (params: {
			standId: number;
			rollsetId: number;
			passPosition: number;
			passChangeReasonId: number;
			comments?: string | null;
			productSizeId?: number;
		}) => {
			const result = await validatePassChange({
				standId: params.standId,
				rollsetId: params.rollsetId,
				passPosition: params.passPosition,
				productSizeId: params.productSizeId,
			});

			if (result === "WARNING") {
				const confirmed = onWarning
					? await onWarning(
							"Pass does not match the product mill setup. Continue?",
						)
					: false;
				if (!confirmed) {
					throw new Error("Pass change cancelled by user");
				}
			}

			await changePass({
				standId: params.standId,
				rollsetId: params.rollsetId,
				passPosition: params.passPosition,
				passChangeReasonId: params.passChangeReasonId,
				comments: params.comments,
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["millSetup"] });
			onSuccess?.();
		},
		onError: (error: Error) => {
			if (error.message !== "Pass change cancelled by user") {
				onError?.(error);
			}
		},
	});

	return {
		changePass: mutation.mutate,
		isChangingPass: mutation.isPending,
	};
};
